import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, Tag, Statistic, Row, Col, Modal, Button, Spin } from 'antd';
import { GiftOutlined, DollarOutlined, UserOutlined, CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const Referrals = ({ userType = 'organizer' }) => {
  const [referrals, setReferrals] = useState([]);
  const [affiliatesData, setAffiliatesData] = useState({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [welcomeModal, setWelcomeModal] = useState(false);
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    loadReferrals();

    // Verificar se é o primeiro acesso às indicações
    const hasSeenWelcome = localStorage.getItem('referrals_welcome_seen');
    if (!hasSeenWelcome) {
      setWelcomeModal(true);
    }
  }, [pagination.current, pagination.pageSize]);



  const loadReferrals = async () => {
    setLoading(true);
    try {
      const endpoint = userType === 'player' ? '/affiliates/player/referrals' : '/affiliates/organizer/referrals';

      const response = await api.get(endpoint, {
        params: {
          page: pagination.current,
          limit: pagination.pageSize
        }
      });

      setReferrals(response.data.data || []);
      setAffiliatesData(response.data.affiliatesData || {});
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0
      }));
    } catch (error) {
      console.error('Erro ao carregar indicações:', error);
      setReferrals([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    }
    setLoading(false);
  };

  const handleTableChange = (paginationInfo) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      total: pagination.total
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      PAID: 'green',
      CANCELLED: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      PENDING: 'Pendente',
      PAID: 'Pago',
      CANCELLED: 'Cancelado'
    };
    return texts[status] || status;
  };

  const handleWelcomeClose = () => {
    localStorage.setItem('referrals_welcome_seen', 'true');
    setWelcomeModal(false);
  };

  const calculateStats = () => {
    const total = referrals.length;
    const pending = referrals.filter(r => r.status === 'PENDING').length;
    const paid = referrals.filter(r => r.status === 'PAID').length;
    const pendingAmount = referrals
      .filter(r => r.status === 'PENDING')
      .reduce((sum, r) => sum + parseFloat(r.commission_amount || 0), 0);
    const totalAmount = referrals
      .reduce((sum, r) => sum + parseFloat(r.commission_amount || 0), 0);

    return { total, pending, paid, pendingAmount, totalAmount };
  };

  const stats = calculateStats();

  const columns = [
    {
      title: 'Time Indicado',
      dataIndex: 'referred_team',
      key: 'referred_team',
    },
    {
      title: 'Email',
      dataIndex: 'referred_email',
      key: 'referred_email',
    },
    {
      title: 'Telefone',
      dataIndex: 'referred_phone',
      key: 'referred_phone',
    },
    {
      title: 'Data da Indicação',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD/MM/YYYY')
    },
    {
      title: 'Valor Original',
      dataIndex: 'original_amount',
      key: 'original_amount',
      render: (amount) => `R$ ${parseFloat(amount || 0).toFixed(2)}`
    },
    {
      title: 'Comissão',
      dataIndex: 'commission_amount',
      key: 'commission_amount',
      render: (amount) => `R$ ${parseFloat(amount || 0).toFixed(2)}`
    },
    {
      title: 'Taxa',
      dataIndex: 'commission_rate',
      key: 'commission_rate',
      render: (rate) => `${rate}%`
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'Data do Pagamento',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Title level={isMobile ? 3 : 2}>
        <GiftOutlined /> Relatório de Indicações
      </Title>

      <Card style={{ marginBottom: 24, backgroundColor: '#f6ffed' }}>
        <Row gutter={16} align="middle">
          <Col>
            <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
              Seu Código de Indicação: {affiliatesData.affiliate_code ? <Tag color="green" style={{ fontSize: 16 }}>{affiliatesData.affiliate_code}</Tag> : <Tag color="orange" style={{ fontSize: 16 }}>Usuário não possui código de indicação</Tag>}
            </Title>
          </Col>
          <Col>
            {affiliatesData.commission_rate ? (
            <Text type="secondary">
              Comissão: {affiliatesData.commission_rate
              ? parseFloat(affiliatesData.commission_rate).toFixed(0)
              : <Spin size="small" />}% por indicação
              <InfoCircleOutlined
                onClick={() => setWelcomeModal(true)}
                style={{color: '#1890ff', cursor: 'help', marginLeft: 8}}
              />
            </Text>
            ) : 
              <>
              <Tag color="orange" style={{ fontSize: 16 }}>Comissão não definida</Tag>
              <InfoCircleOutlined
                onClick={() => setWelcomeModal(true)}
                style={{color: '#1890ff', cursor: 'help', marginLeft: 0, paddingRight: 0}}
              />
              </>
            }
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total de Indicações"
              value={stats.total}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pendentes"
              value={stats.pending}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pagas"
              value={stats.paid}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total a Receber"
              value={stats.pendingAmount}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={referrals}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} indicações`,
            onChange: handleTableChange
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Modal de Boas-vindas ao Sistema de Indicações */}
      <Modal
        title={<><InfoCircleOutlined style={{ color: '#1890ff' }} /> Sistema de Indicações Futezap!</>}
        open={welcomeModal}
        onCancel={handleWelcomeClose}
        footer={[
          <Button key="start" type="primary" onClick={handleWelcomeClose}>
            Começar a Indicar
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <GiftOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </div>

          <Typography.Paragraph style={{ fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            O <strong>Sistema de Indicações</strong> permite que você ganhe comissões indicando novos {userType === 'player' ? 'jogadores' : 'organizadores'} para o Futezap.
          </Typography.Paragraph>

          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>
              <DollarOutlined style={{ color: '#52c41a' }} /> Como Funciona:
            </Typography.Title>
            <ul style={{ paddingLeft: 20 }}>
              <li>Compartilhe o seu <strong>código único</strong> com outros {userType === 'player' ? 'jogadores' : 'organizadores'}</li>
              <li>Quando um {userType === 'player' ? 'jogador' : 'organizador'} se cadastrar usando o seu código, você ganha uma comissão de <strong>{affiliatesData.commission_rate ? `${parseFloat(affiliatesData.commission_rate).toFixed(0)}` : '??'}% sobre a 1ª mensalidade</strong> do indicado</li>
              <li>A comissão é paga uma única vez quando recebermos a <strong>primeira mensalidade</strong> do indicado</li>
              <li>Você pode acompanhar todas as suas indicações acessando o menu <p style={{whiteSpace: "nowrap"}}>"<GiftOutlined/> Indicações"</p></li>
            </ul>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>
              <CheckCircleOutlined style={{ color: '#faad14' }} /> Status das Comissões:
            </Typography.Title>
            <ul style={{ paddingLeft: 20 }}>
              <li><strong>Pendente</strong> - Aguardando pagamento da primeira mensalidade</li>
              <li><strong>Pago</strong> - Comissão já foi paga para você</li>
              <li><strong>Cancelado</strong> - Indicação foi cancelada</li>
            </ul>
          </div>

          <div style={{
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 6,
            padding: 16,
            textAlign: 'center'
          }}>
            <Typography.Text strong style={{ color: '#d46b08' }}>
              💰 Dica: Quanto mais você indicar, mais você ganhará! Compartilhe seu código!
            </Typography.Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Referrals;